import React, { useState, useEffect, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { ArrowBigLeft, Dices } from 'lucide-react';
import { io } from 'socket.io-client';

import "./LudoGame.scss";

const LudoGame = ({ tableId, username, playerCave }) => {
    const [gameState, setGameState] = useState({
        board: null,
        players: [],
        currentTurnIndex: null, // Index de 0 à 3
        diceValue: null,
        isRolling: false
    });
    const socketRef = useRef(null);

    useEffect(() => {
        // Initialize socket connection
        const socket = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000');
        socketRef.current = socket;

        socket.on('connect', () => {
            const emitData = { tableId, username, playerCave };
            socket.emit('join_ludo_table', emitData);
        });

        socket.on('tableStateUpdate', (data) => {
            setGameState(prev => ({ 
                ...prev, 
                players: data.players,
                // Si l'index est fourni lors de la mise à jour, mettez-le à jour
                currentTurnIndex: data.currentTurnIndex !== undefined ? data.currentTurnIndex : prev.currentTurnIndex 
            }));
        });

        // ✅ Gérer l'état initial lors du join
        socket.on('join_ludo_table', (data) => {
            if (data.currentTurnIndex !== undefined) {
                setGameState(prev => ({ ...prev, currentTurnIndex: data.currentTurnIndex }));
            }
        });

        socket.on('gameStarted', (data) => {
            setGameState(prev => ({ ...prev, currentTurnIndex: data.currentTurnIndex }));
        });

        socket.on('diceRolled', (data) => {
            setGameState(prev => ({ 
                ...prev, 
                diceValue: data.diceResult, 
                currentTurnIndex: data.nextTurnIndex, 
                isRolling: false 
            }));
        });

        return () => {
            socket.disconnect();
        };
    }, [tableId, username, playerCave]);

    const rollDice = () => {
        
        if (gameState.isRolling) {
            return;
        }
        if (!isMyTurn) {
            return;
        }
        
        if (!socketRef.current) {
            return;
        }

        setGameState(prev => ({ ...prev, isRolling: true }));
        socketRef.current.emit('rollDice', { tableId });
    };

    // Helper pour savoir si une cellule fait partie d'une base spécifique
    const getBaseColor = (row, col) => {
        if (row <= 6 && col <= 6) return 'red';
        if (row <= 6 && col >= 10) return 'green';
        if (row >= 10 && col <= 6) return 'yellow';
        if (row >= 10 && col >= 10) return 'blue';
        return null;
    };

    // Identifiant unique pour ce joueur dans cette table
    const playerIndex = gameState.players.findIndex(p => p.username === username);

    // ✅ FIX: Calculer le statut du tour de manière robuste
    const isMyTurn = gameState.currentTurnIndex !== null && 
                     playerIndex !== -1 && 
                     playerIndex === gameState.currentTurnIndex;

    // ✅ FIX: Récupérer le username de manière robuste
    const currentPlayer = gameState.currentTurnIndex !== null ? gameState.players[gameState.currentTurnIndex] : null;
    const currentPlayerName = currentPlayer ? (currentPlayer.username || "Inconnu") : "Chargement...";
    

    return (
        <div className="ludo-game-container" style={{ position: 'relative' }}>
            <ToastContainer />
            
            <div className="ludo-header">
                <h2>Table Ludo #{tableId}</h2>
                <div className="game-status">
                    {gameState.players.length < 2 
                        ? `En attente de joueurs... (${gameState.players.length}/2)` 
                        : isMyTurn ? "À votre tour !" : `Tour du joueur ${currentPlayerName}`}
                </div>
            </div>

            <div className="ludo-board-wrapper" style={{ position: 'relative', width: 'fit-content', margin: '0 auto' }}>
                <div className="ludo-board">
                    {Array.from({ length: 225 }).map((_, i) => {
                        const row = Math.floor(i / 15) + 1;
                        const col = (i % 15) + 1;
                        const baseColor = getBaseColor(row, col);
                        const className = `board-cell ${baseColor ? baseColor + '-base' : ''}`;
                        
                        // Détecter si cette cellule est le "centre" du garage pour afficher les infos
                        const isGarageCenter = baseColor && (row === 3 && col === 3 || row === 3 && col === 13 || row === 13 && col === 3 || row === 13 && col === 13);
                        const playerInGarage = isGarageCenter ? gameState.players.find(p => p.color === baseColor) : null;
                        
                        return (
                            <div key={i} className={className} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                {playerInGarage ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', width: '100%', pointerEvents: 'none', zIndex: 10, background: 'rgba(255,255,255,0.8)', padding: '2px', borderRadius: '4px' }}>
                                        {/* 2 pawns top */}
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            {[0, 1].map(pawnIdx => (
                                                <div key={pawnIdx} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: playerInGarage.color, border: '1px solid black' }}></div>
                                            ))}
                                        </div>
                                        
                                        {/* Username */}
                                        <span style={{ fontSize: '9px', fontWeight: 'bold', color: 'black' }}>{playerInGarage.username}</span>
                                        
                                        {/* 2 pawns bottom */}
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            {[2, 3].map(pawnIdx => (
                                                <div key={pawnIdx} style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: playerInGarage.color, border: '1px solid black' }}></div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
            
            <div className="ludo-controls">
                <button 
                    className="btn-roll" 
                    onClick={rollDice}
                    // Force-enabled temporarily to test if the button click works
                    disabled={false} 
                >
                    <Dices size={24} />
                    {gameState.isRolling ? "Lancement..." : "Lancer le dé"}
                </button>
                {gameState.diceValue && <div className="dice-result">Résultat du dé: {gameState.diceValue}</div>}
                <div style={{fontSize: '10px', color: 'red'}}>
                    DEBUG: isMyTurn={isMyTurn.toString()}, isRolling={gameState.isRolling.toString()}, playerIndex={playerIndex}, currentTurn={gameState.currentTurnIndex}
                </div>
            </div>

            <div className="ludo-exit" onClick={() => {/* Logique de sortie */}}>
                <ArrowBigLeft /> Quitter
            </div>
        </div>
    );
};

export default LudoGame;
